import type {
  EsdmAcquisitionStatus,
  EsdmDomainCode,
  EsdmDomainRollup,
  EsdmItem,
  EsdmItemProgress,
  EsdmItemResult,
  EsdmLevel,
} from '../types/esdm';

/** Canonical key for a level+domain pair, e.g. for `EsdmProgressDoc.domainSynthesis`. */
export function esdmDomainKey(level: EsdmLevel, domain: EsdmDomainCode): string {
  return `${level}:${domain}`;
}

/**
 * Maps a per-item score to its acquisition status. Only "Adquirido" counts as
 * acquired; "Parcial" and "Não adquirido" are not-yet-acquired; "Sem avaliação"
 * and an unscored item are not started.
 */
export function statusFromResult(result: EsdmItemResult | undefined): EsdmAcquisitionStatus {
  if (result === 'A') return 'ADQUIRIDO';
  if (result === 'PA' || result === 'NA') return 'NAO_ADQUIRIDO';
  return 'NAO_INICIADO';
}

export function getEsdmDomainRollup(
  items: EsdmItem[],
  progress: Record<string, EsdmItemProgress>
): EsdmDomainRollup[] {
  const groups = new Map<string, EsdmItem[]>();
  for (const item of items) {
    const key = esdmDomainKey(item.level, item.domain);
    const list = groups.get(key);
    if (list) list.push(item);
    else groups.set(key, [item]);
  }

  const rollups: EsdmDomainRollup[] = [];
  for (const [key, groupItems] of groups) {
    const [levelStr, domain] = key.split(':');
    let acquired = 0;
    let notAcquired = 0;
    let notStarted = 0;
    let filled = 0;
    for (const item of groupItems) {
      const status = progress[item.id]?.status ?? 'NAO_INICIADO';
      if (status === 'ADQUIRIDO') acquired += 1;
      else if (status === 'NAO_ADQUIRIDO') notAcquired += 1;
      else notStarted += 1;
      if (progress[item.id]?.result !== undefined) filled += 1;
    }
    const total = groupItems.length;
    rollups.push({
      level: Number(levelStr) as EsdmDomainRollup['level'],
      domain: domain as EsdmDomainRollup['domain'],
      acquired,
      notAcquired,
      notStarted,
      filled,
      total,
      pctAcquired: total > 0 ? acquired / total : 0,
    });
  }

  return rollups.sort((a, b) => a.level - b.level || a.domain.localeCompare(b.domain));
}
